import { ComplianceTrendCharts } from '@/components/admin/TrendCharts';
import { ColumnPicker, useColumnVisibility, type ColDef } from '@/components/ui/ColumnPicker';
import { useDashboardNormasDetalle, useDashboardTrend } from '@/hooks';
import { Award, Building2, CheckCircle, Clock } from 'lucide-react';
import { useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';

function formatHours(h: number): string {
  const t = Math.round(h * 3600);
  const hrs = Math.floor(t / 3600);
  const min = Math.floor((t % 3600) / 60);
  if (hrs === 0) return `${min}m`;
  return `${hrs}h ${min}m`;
}

function ProgBar({ value, color, fill, decimals = 2 }: { value: number; color: string; fill?: boolean; decimals?: number }) {
  return (
    <span className={`inline-flex items-center gap-1.5 ${fill ? 'w-full' : ''}`}>
      <span className={`admin-prog-bar bg-white ${fill ? 'flex-1' : ''}`}>
        <span className="admin-prog-fill" style={{ width: `${Math.min(value, 100)}%`, background: color }} />
      </span>
      <span className="text-[12px] font-medium">{value.toFixed(decimals)}%</span>
    </span>
  );
}

function pColor(v: number): string {
  if (v >= 80) return 'var(--admin-green)';
  if (v >= 50) return '#e09b00';
  return 'var(--admin-red)';
}

type NormaEmpresaColKey = 'empresa' | 'nit' | 'asesor' | 'cumplimientoNorma' | 'cumplimientoGeneral' | 'horas';

const NORMA_EMPRESA_COLS: ColDef<NormaEmpresaColKey>[] = [
  { key: 'empresa', label: 'Empresa', defaultVisible: true },
  { key: 'nit', label: 'NIT', defaultVisible: true },
  { key: 'asesor', label: 'Asesor', defaultVisible: true },
  { key: 'cumplimientoNorma', label: 'Cumpl. Norma', defaultVisible: true },
  { key: 'cumplimientoGeneral', label: 'Cumpl. Gral.', defaultVisible: true },
  { key: 'horas', label: 'Horas (Aprob.)', defaultVisible: true },
];

export function AdminNormasPage() {
  const navigate = useNavigate();
  const { ntcId } = useParams<{ ntcId: string }>();
  const { data: normas, isLoading } = useDashboardNormasDetalle();
  const { data: trendData, isLoading: loadingTrend, fetchTrend } = useDashboardTrend();
  const [selectedNtcId, setSelectedNtcId] = useState<number | null>(null);

  const {
    visible: colVisible,
    toggle: toggleCol,
    reset: resetCols,
    activeCount: colCount,
  } = useColumnVisibility<NormaEmpresaColKey>(NORMA_EMPRESA_COLS, 'tourixia:admin-normas-cols');

  // Sincronizar selectedNtcId con el parámetro de la URL
  useEffect(() => {
    if (ntcId) {
      setSelectedNtcId(Number(ntcId));
    }
  }, [ntcId]);

  const activeNorma = useMemo(() => {
    if (!normas || normas.length === 0) return null;
    if (selectedNtcId === null) return normas[0];
    return normas.find((n) => n.ntcId === selectedNtcId) || normas[0];
  }, [normas, selectedNtcId]);

  const promedioGeneralPct = useMemo(() => {
    if (!activeNorma || activeNorma.totalEmpresas === 0) return 0;
    const sum = activeNorma.empresas.reduce((acc, e) => acc + e.cumplimientoGeneral, 0);
    return sum / activeNorma.totalEmpresas;
  }, [activeNorma]);

  if (isLoading) {
    return (
      <div className="p-12 text-center">
        <div className="w-6 h-6 border-2 border-(--admin-blue) border-t-transparent rounded-full animate-spin mx-auto mb-3" />
        <p className="text-[12px] text-(--admin-text3)">Cargando detalle de normas…</p>
      </div>
    );
  }

  if (!normas || normas.length === 0) {
    return <div className="admin-card p-12 text-center text-(--admin-text3)">No hay datos de normas disponibles en este momento.</div>;
  }

  const avanceHorasGlobalPct = activeNorma ? (activeNorma.promedioHoras / 320) * 100 : 0;

  return (
    <div className="space-y-6">
      {/* Selector de Norma */}
      <div className="admin-search-row">
        <div className="flex items-center gap-3">
          <Award className="w-5 h-5 text-(--admin-text2)" />
          <span className="text-[14px] font-medium text-(--admin-text)">Seleccionar NTC:</span>
        </div>
        <select
          value={activeNorma?.ntcId || ''}
          onChange={(e) => {
            const val = e.target.value;
            setSelectedNtcId(Number(val));
            navigate(`/normas/${val}`);
          }}
          className="admin-select w-64 max-w-full"
        >
          {normas.map((n) => (
            <option key={n.ntcId} value={n.ntcId}>
              {n.nombre} ({n.totalEmpresas} empresas)
            </option>
          ))}
        </select>
      </div>

      {activeNorma && (
        <>
          {/* Resumen de la norma seleccionada */}
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
            <div className="admin-metric-card">
              <div className="text-[12px] text-(--admin-text3) font-medium mb-1 flex items-center gap-1.5">
                <Building2 className="w-3.5 h-3.5" /> Empresas Asignadas
              </div>
              <div className="text-[24px] font-bold text-(--admin-text)">{activeNorma.totalEmpresas}</div>
              <div className="text-[11px] text-(--admin-text3) mt-1">Con esta NTC en su plan</div>
            </div>

            <div className="admin-metric-card">
              <div className="text-[12px] text-(--admin-text3) font-medium mb-1 flex items-center gap-1.5">
                <CheckCircle className="w-3.5 h-3.5" /> Cumplimiento Gral. Promedio
              </div>
              <div className="text-[24px] font-bold" style={{ color: pColor(promedioGeneralPct) }}>
                {promedioGeneralPct.toFixed(1)}%
              </div>
              <div className="text-[11px] text-(--admin-text3) mt-1">Promedio del total de las normas</div>
              <div className="mt-1">
                <ProgBar value={promedioGeneralPct} color={pColor(promedioGeneralPct)} fill />
              </div>
            </div>

            <div className="admin-metric-card">
              <div className="text-[12px] text-(--admin-text3) font-medium mb-1 flex items-center gap-1.5">
                <Award className="w-3.5 h-3.5" /> Cumplimiento {activeNorma.nombre}
              </div>
              <div className="text-[24px] font-bold" style={{ color: pColor(activeNorma.promedioCumplimiento) }}>
                {activeNorma.promedioCumplimiento.toFixed(1)}%
              </div>
              <div className="text-[11px] text-(--admin-text3) mt-1">Promedio específico de la norma</div>
              <div className="mt-1">
                <ProgBar value={activeNorma.promedioCumplimiento} color={pColor(activeNorma.promedioCumplimiento)} fill />
              </div>
            </div>

            <div className="admin-metric-card">
              <div className="text-[12px] text-(--admin-text3) font-medium mb-1 flex items-center gap-1.5">
                <Clock className="w-3.5 h-3.5" /> Avance Promedio de Horas
              </div>
              <div className="text-[24px] font-bold" style={{ color: pColor(avanceHorasGlobalPct) }}>
                {avanceHorasGlobalPct.toFixed(1)}%
              </div>
              <div className="text-[11px] text-(--admin-text3) mt-1">
                {formatHours(activeNorma.promedioHoras * activeNorma.totalEmpresas)} / {320 * activeNorma.totalEmpresas}h
              </div>
              <div className="mt-1">
                <ProgBar value={avanceHorasGlobalPct} color={pColor(avanceHorasGlobalPct)} fill />
              </div>
            </div>
          </div>

          {/* Tabla de empresas */}
          <div className="admin-card">
            <div className="px-5 py-4 border-b border-(--admin-border) flex items-center justify-between">
              <h3 className="font-semibold text-(--admin-text) text-[14px]">Desglose por Empresas: {activeNorma.nombre}</h3>
              <ColumnPicker
                cols={NORMA_EMPRESA_COLS}
                visible={colVisible}
                onToggle={toggleCol}
                onReset={resetCols}
                activeCount={colCount}
              />
            </div>
            <table className="admin-table">
              <thead>
                <tr>
                  {colVisible.empresa && <th>Empresa</th>}
                  {colVisible.nit && <th>NIT</th>}
                  {colVisible.asesor && <th>Asesor</th>}
                  {colVisible.cumplimientoNorma && <th>Cumplimiento {activeNorma.nombre}</th>}
                  {colVisible.cumplimientoGeneral && <th>Cumplimiento Gral.</th>}
                  {colVisible.horas && <th>Horas (Aprobadas)</th>}
                  <th className="w-10"></th>
                </tr>
              </thead>
              <tbody>
                {activeNorma.empresas.map((e) => (
                  <tr key={e.nitId} className="clickable" onClick={() => navigate(`/empresas/${e.nitId}`)}>
                    {colVisible.empresa && <td className="font-medium text-(--admin-text)">{e.nombre}</td>}
                    {colVisible.nit && <td className="font-mono text-[11px] text-(--admin-text3)">{e.nitId}</td>}
                    {colVisible.asesor && <td className="text-(--admin-text2)">{e.asesor}</td>}
                    {colVisible.cumplimientoNorma && (
                      <td>
                        <ProgBar value={e.cumplimientoNorma} color={pColor(e.cumplimientoNorma)} />
                      </td>
                    )}
                    {colVisible.cumplimientoGeneral && (
                      <td>
                        <ProgBar value={e.cumplimientoGeneral} color={pColor(e.cumplimientoGeneral)} />
                      </td>
                    )}
                    {colVisible.horas && <td className="text-(--admin-text2)">{formatHours(e.horasTotales)}</td>}
                    <td>
                      <button
                        className="admin-btn text-[11px] py-1 px-2.5"
                        onClick={(ev) => {
                          ev.stopPropagation();
                          navigate(`/empresas/${e.nitId}`);
                        }}
                      >
                        Ver →
                      </button>
                    </td>
                  </tr>
                ))}
                {activeNorma.empresas.length === 0 && (
                  <tr>
                    <td
                      colSpan={NORMA_EMPRESA_COLS.filter((c) => colVisible[c.key]).length + 1}
                      className="text-center py-8 text-(--admin-text3)"
                    >
                      <Building2 className="w-8 h-8 mx-auto mb-2 opacity-30" />
                      Ninguna empresa asignada a esta norma
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </>
      )}

      {/* Historical Evolution Analysis */}
      <div className="flex items-center justify-between mt-12 mb-4 border-b border-(--admin-border) pb-4">
        <div>
          <h2 className="text-[14px] font-bold text-(--admin-text) uppercase tracking-tight">Análisis de Evolución Histórica</h2>
          <p className="text-[11px] text-(--admin-text3) mt-1">
            Evolución del cumplimiento promedio de todas las empresas por tipo de norma (N1, N2, N3)
          </p>
        </div>
        <button
          onClick={fetchTrend}
          disabled={loadingTrend}
          className={`flex items-center gap-2 px-4 py-2 rounded-lg text-[11px] font-bold transition-all border ${
            loadingTrend
              ? 'bg-gray-100 text-gray-400 border-gray-200 cursor-not-allowed'
              : 'bg-(--admin-blue-bg) text-(--admin-blue) border-(--admin-blue) hover:bg-(--admin-blue) hover:text-white'
          }`}
        >
          {loadingTrend ? (
            <>
              <div className="w-3 h-3 border-2 border-current border-t-transparent rounded-full animate-spin" />
              Generando análisis profundo...
            </>
          ) : (
            <>Generar Análisis Profundo</>
          )}
        </button>
      </div>

      <ComplianceTrendCharts tendencia={trendData || []} />
    </div>
  );
}
